import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from '@context/AuthContext';
import { PageContextProvider } from '@context/PageContext';

// Import pages
import Home from '@pages/Home/Home';
import Login from '@pages/Login/Login';
import Register from '@pages/Register/Register';
import ConnectBrokerage from '@pages/ConnectAlpaca/ConnectAlpaca';
import PrivacyPolicy from '@pages/PrivacyPolicy/PrivacyPolicy';
import TermsAndConditions from '@pages/TermsAndConditions/TermsAndConditions';
import BetaTesterAgreement from '@pages/BetaTesterAgreement/BetaTesterAgreement';
import Asset from '@pages/Asset/Asset';
import Cryptocurrency from '@pages/Cryptocurrency/Cryptocurrency';
import AssetIndex from '@pages/AssetIndex/AssetIndex';
import AssetOption from '@pages/AssetOption/AssetOption';
import OAuthCallback from '@pages/OAuthCallback/OAuthCallback';

// Import Tools
import RequestReport from '@pages/Tools/RequestReport/RequestReport';

// Import Admin Tools
import BacktestTrades from '@pages/Admin/Tools/BacktestTrades/BacktestTrades';
import FilterBacktests from '@pages/Admin/Tools/FilterBacktests/FilterBacktests';
import BacktestStatusRedirect from '@pages/Admin/Tools/BacktestStatusRedirect';
import BacktestParameterAnalysis from '@pages/Admin/Tools/BacktestParameterAnalysis/BacktestParameterAnalysis';
import BacktestSummary from '@pages/Admin/Tools/BacktestSummary/BacktestSummary';
import ScreenerList from '@pages/Admin/Tools/Screeners/ScreenerList';
import ScreenerEditor from '@pages/Admin/Tools/Screeners/ScreenerEditor';
import ChartPatternAudit from '@pages/Admin/Tools/ChartPatternAudit/ChartPatternAudit';
import ChartPatternAuditRender from '@pages/Admin/Tools/ChartPatternAudit/ChartPatternAuditRender';
import ChartPatternAssetOracleRender from '@pages/Admin/Tools/ChartPatternAudit/ChartPatternAssetOracleRender';
import ElliottWaveAudit from '@pages/Admin/Tools/ElliottWaveAudit/ElliottWaveAudit';
import ElliottWaveAuditRender from '@pages/Admin/Tools/ElliottWaveAudit/ElliottWaveAuditRender';
import OBVAudit from '@pages/Admin/Tools/OBVAudit/OBVAudit';
import OBVAuditRender from '@pages/Admin/Tools/OBVAudit/OBVAuditRender';
import SupportSessions from '@pages/Admin/Tools/SupportSessions/SupportSessions';
import VolumePriceAnalysisAudit from '@pages/Admin/Tools/VolumePriceAnalysisAudit/VolumePriceAnalysisAudit';

// Import User pages
import Portfolio from '@pages/User/Portfolio/Portfolio';
import Portfolios from '@pages/User/Portfolios/Portfolios';
import PortfolioPerformance from '@pages/User/PortfolioPerformance/PortfolioPerformance';
import PortfolioTradeHistory from '@pages/User/PortfolioTradeHistory/PortfolioTradeHistory';
import PortfolioPendingTrades from '@pages/User/PortfolioPendingTrades/PortfolioPendingTrades';
import PortfolioSettings from '@pages/User/PortfolioSettings/PortfolioSettings';
import StrategySubscriptions from '@pages/User/StrategySubscriptions/StrategySubscriptions';
import PortfolioBacktest from '@pages/User/PortfolioBacktest/PortfolioBacktest';
import EntityLists from '@pages/User/EntityLists/EntityLists';
import RealTimeEntries from '@pages/User/RealTimeEntries/RealTimeEntries';
import EntryLog from '@pages/User/EntryLog/EntryLog';
import TradeLogicExecutionJournal from '@pages/User/TradeLogicExecutionJournal/TradeLogicExecutionJournal';
import TradeLogicExecutionJournals from '@pages/User/TradeLogicExecutionJournals/TradeLogicExecutionJournals';
import VisualizeTrade from '@pages/User/VisualizeTrade/VisualizeTrade';
import TradingProfiles from '@pages/User/TradingProfiles/TradingProfiles';
import Enable2FA from '@pages/User/Enable2FA/Enable2FA';
import PortfolioBacktestTrades from '@/pages/User/PortfolioBacktestTrades/PortfolioBacktestTrades';
import PortfolioBacktestStats from '@pages/User/PortfolioBacktestStats/PortfolioBacktestStats';
import TradingStrategies from '@pages/User/TradingStrategies/TradingStrategies';
import StrategyFamilyView from '@pages/User/StrategyFamilies/StrategyFamilyView';
import StrategyVariantComparisons from '@pages/User/StrategyVariantComparisons/StrategyVariantComparisons';
import TradingStrategyEditor from '@pages/User/TradingStrategyEditor/TradingStrategyEditor';
import Aero from '@pages/User/Aero/Aero';
import EntryOptimizer from '@pages/User/EntryOptimizer/EntryOptimizer';
import EntryOptimizerNew from '@pages/User/EntryOptimizer/EntryOptimizerNew';
import EntryOptimizerDetails from '@pages/User/EntryOptimizer/EntryOptimizerDetails';
import EntryOptimizerStudyNew from '@pages/User/EntryOptimizer/EntryOptimizerStudyNew';
import EntryOptimizerStudyDetails from '@pages/User/EntryOptimizer/EntryOptimizerStudyDetails';
import IdeaLab from '@pages/User/IdeaLab/IdeaLab';
import IdeaLabStudy from '@pages/User/IdeaLab/IdeaLabStudy';
import IdeaLabVariant from '@pages/User/IdeaLab/IdeaLabVariant';
import IdeaLabRun from '@pages/User/IdeaLab/IdeaLabRun';
import IdeaLabEntryReview from '@pages/User/IdeaLab/IdeaLabEntryReview';
import AeroNew from '@pages/User/Aero/AeroNew';
import AeroDetails from '@pages/User/Aero/AeroDetails';
import TradeLogicLibraryPage from '@pages/User/TradeLogic/TradeLogicLibraryPage';
import TradeLogicEditorPage from '@pages/User/TradeLogicEditor/TradeLogicEditorPage';
import ExitCandidates from '@pages/User/ExitCandidates/ExitCandidates';
import OptionBuyingStrategies from '@pages/User/OptionBuyingStrategies/OptionBuyingStrategies';
import OptionBuyingStrategyEditor from '@pages/User/OptionBuyingStrategyEditor/OptionBuyingStrategyEditor';
import StrategyReadiness from '@pages/User/StrategyReadiness';
import TradingAccounts from '@pages/User/TradingAccounts/TradingAccounts';
import ThirdPartyConnections from '@pages/User/ThirdPartyConnections/ThirdPartyConnections';
import DeveloperAccess from '@pages/User/DeveloperAccess/DeveloperAccess';
import AIApprovals from '@pages/User/AIApprovals/AIApprovals';
import Experiments from '@pages/User/Experiments/Experiments';
import ExperimentCompare from '@pages/User/Experiments/ExperimentCompare';
import ExperimentEditor from '@pages/User/Experiments/ExperimentEditor';
import ExperimentResults from '@pages/User/Experiments/ExperimentResults';
import ExperimentEntityResults from '@pages/User/Experiments/ExperimentEntityResults';
import ChatPage from '@pages/User/Chat/Chat';
import AlphaAtlasReport from '@pages/User/AlphaAtlas/AlphaAtlasReport';
import AlphaAtlasRunHealth from '@pages/Admin/Tools/AlphaAtlas/AlphaAtlasRunHealth';
import IntelCallReview from '@pages/Admin/Tools/IntelCallReview/IntelCallReview';

import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './api/trpc';
import ScorecardsManager from '@pages/User/Scorecards/ScorecardsManager';
import ScorecardEditor from '@pages/User/Scorecards/ScorecardEditor';
import TradingProfileEditor from './pages/User/TradingProfileEditor/TradingProfileEditor.tsx';

function App() {
    return (
        <Router>
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <PageContextProvider>
                        <Routes>
                            {/* Main routes */}
                            <Route element={<Home />} path="/" />
                            <Route element={<Login />} path="/login" />
                            <Route element={<Register />} path="/register" />
                            <Route element={<ConnectBrokerage />} path="/connect/brokerage" />
                            <Route element={<PrivacyPolicy />} path="/privacy-policy" />
                            <Route element={<TermsAndConditions />} path="/terms-and-conditions" />
                            <Route element={<BetaTesterAgreement />} path="/beta-tester-agreement" />

                            {/* Entity routes */}
                            <Route element={<Asset />} path="/asset/:id" />
                            <Route element={<AssetOption />} path="/asset/:id/option/:optionId" />
                            <Route element={<Cryptocurrency />} path="/cryptocurrency/:id" />
                            <Route element={<AssetIndex />} path="/assetindex/:id" />

                            {/* User routes */}
                            <Route element={<Portfolio />} path="/user/portfolio/:id" />
                            <Route element={<Portfolios />} path="/user/portfolios" />
                            <Route element={<PortfolioPerformance />} path="/user/portfolioPerformance/:id" />
                            <Route element={<PortfolioTradeHistory />} path="/user/portfolioTradeHistory/:id" />
                            <Route element={<PortfolioPendingTrades />} path="/user/portfolioPendingTrades/:id" />
                            <Route element={<PortfolioSettings />} path="/user/portfolioSettings/:id" />
                            <Route element={<StrategySubscriptions />} path="/user/strategySubscriptions" />
                            <Route element={<TradingProfileEditor />} path="/user/portfolioTradingProfile/:id" />
                            <Route element={<PortfolioBacktest />} path="/user/portfolioBacktest" />
                            <Route element={<PortfolioBacktest />} path="/user/portfolioBacktest/:id" />
                            <Route element={<EntityLists />} path="/user/entityLists" />
                            <Route element={<RealTimeEntries />} path="/user/realTimeEntries" />
                            <Route element={<EntryLog />} path="/user/entryLog" />
                            <Route element={<TradeLogicExecutionJournals />} path="/user/tradeLogicExecutionJournals" />
                            <Route element={<TradeLogicExecutionJournal />} path="/user/tradeLogicExecutionJournal/:id" />
                            <Route element={<VisualizeTrade />} path="/user/visualizeTrade/:id" />
                            <Route element={<TradingProfiles />} path="/user/tradingProfiles" />
                            <Route element={<Enable2FA />} path="/user/enable2fa" />
                            <Route element={<PortfolioBacktestTrades />} path="/user/portfolioBacktestTrades" />
                            <Route element={<PortfolioBacktestStats />} path="/user/portfolioBacktestStats" />
                            <Route element={<TradingStrategies />} path="/user/tradingStrategies" />
                            <Route element={<StrategyFamilyView />} path="/user/strategyFamilies/:familyId" />
                            <Route element={<StrategyVariantComparisons />} path="/user/strategyVariantComparisons" />
                            <Route element={<TradingStrategyEditor />} path="/user/tradingStrategy/:id" />
                            <Route element={<EntryOptimizer />} path="/user/entryOptimizer" />
                            <Route element={<EntryOptimizerNew />} path="/user/entryOptimizer/new" />
                            <Route element={<EntryOptimizerStudyNew />} path="/user/entryOptimizer/studies/new" />
                            <Route element={<EntryOptimizerStudyDetails />} path="/user/entryOptimizer/studies/:studyId" />
                            <Route element={<EntryOptimizerDetails />} path="/user/entryOptimizer/:jobId" />
                            <Route element={<IdeaLab />} path="/user/ideaLab" />
                            <Route element={<IdeaLabStudy />} path="/user/ideaLab/study/:studyId" />
                            <Route element={<IdeaLabVariant />} path="/user/ideaLab/variant/:variantId" />
                            <Route element={<IdeaLabRun />} path="/user/ideaLab/run/:runId" />
                            <Route element={<IdeaLabEntryReview />} path="/user/ideaLab/run/:runId/review" />
                            <Route element={<Aero />} path="/user/aero" />
                            <Route element={<AeroNew />} path="/user/aero/new" />
                            <Route element={<AeroDetails />} path="/user/aero/:jobId" />
                            <Route element={<Navigate replace to="/user/aero" />} path="/user/exitOptimization" />
                            <Route element={<Navigate replace to="/user/aero/new" />} path="/user/exitOptimization/new" />
                            <Route element={<LegacyExitOptimizationRedirect />} path="/user/exitOptimization/:jobId" />
                            <Route element={<TradeLogicLibraryPage />} path="/user/tradeLogic" />
                            <Route element={<TradeLogicEditorPage />} path="/user/tradeLogic/:kind/:id" />
                            <Route element={<ExitCandidates />} path="/user/exitCandidates" />
                            <Route element={<ExitCandidates />} path="/user/exitCandidate/:candidateId" />
                            <Route element={<OptionBuyingStrategies />} path="/user/optionBuyingStrategies" />
                            <Route element={<OptionBuyingStrategyEditor />} path="/user/optionBuyingStrategy/:id" />
                            <Route element={<StrategyReadiness />} path="/user/strategyDashboard/:id" />
                            <Route element={<TradingAccounts />} path="/user/tradingAccounts" />
                            <Route element={<ThirdPartyConnections />} path="/user/thirdPartyConnections" />
                            <Route element={<DeveloperAccess />} path="/user/developerAccess" />
                            <Route element={<AIApprovals />} path="/user/aiApprovals" />
                            <Route element={<Experiments />} path="/user/experiments" />
                            <Route element={<ExperimentCompare />} path="/user/experimentCompare" />
                            <Route element={<ExperimentEditor />} path="/user/experiment/new" />
                            <Route element={<ExperimentEditor />} path="/user/experiment/:id" />
                            <Route element={<ExperimentResults />} path="/user/experimentResults/:id" />
                            <Route element={<ExperimentEntityResults />} path="/user/experimentResults/:id/entities" />
                            <Route element={<ChatPage />} path="/user/chat" />
                            <Route element={<ChatPage />} path="/user/chat/new" />
                            <Route element={<ChatPage />} path="/user/chat/:sessionId" />
                            <Route element={<AlphaAtlasReport />} path="/user/alphaAtlas" />

                            {/* Scorecards */}
                            <Route element={<ScorecardsManager />} path="/user/scorecards" />
                            <Route element={<ScorecardEditor />} path="/user/scorecard/:id" />

                            {/* Tools routes */}
                            <Route element={<RequestReport />} path="/tools/requestReport" />

                            {/* Admin Tools routes */}
                            <Route element={<BacktestTrades />} path="/tools/backtestTrades" />
                            <Route element={<BacktestSummary />} path="/tools/backtestSummary" />
                            <Route element={<FilterBacktests />} path="/tools/filterBacktests" />
                            {/* Retired: strategy backtests now live on the strategy page. Kept for old links and bookmarks. */}
                            <Route element={<BacktestStatusRedirect />} path="/tools/backtestStatus" />
                            <Route element={<BacktestParameterAnalysis />} path="/tools/backtestParameterAnalysis" />
                            <Route element={<ScreenerList />} path="/tools/screeners" />
                            <Route element={<ScreenerEditor />} path="/tools/screener/:id" />
                            <Route element={<ChartPatternAudit />} path="/tools/chartPatternAudit" />
                            <Route element={<ChartPatternAuditRender />} path="/tools/chartPatternAudit/render/:auditItemId" />
                            <Route element={<ChartPatternAssetOracleRender />} path="/tools/chartPatternAudit/rawAssetRender/:assetId" />
                            <Route element={<ElliottWaveAudit />} path="/tools/elliottWaveAudit" />
                            <Route element={<ElliottWaveAuditRender />} path="/tools/elliottWaveAudit/render/:assetId" />
                            <Route element={<OBVAudit />} path="/tools/obvAudit" />
                            <Route element={<OBVAuditRender />} path="/tools/obvAudit/render/:entityId" />
                            <Route element={<SupportSessions />} path="/tools/supportSessions" />
                            <Route element={<VolumePriceAnalysisAudit />} path="/tools/vpaAudit" />
                            <Route element={<AlphaAtlasRunHealth />} path="/tools/alphaAtlas" />
                            <Route element={<IntelCallReview />} path="/tools/intelCallReview" />

                            {/* OAuth Callback routes */}
                            <Route element={<OAuthCallback />} path="/oauthCallback/:id" />

                            {/* Catch-all route */}
                            <Route element={<Navigate replace to="/" />} path="*" />
                        </Routes>
                    </PageContextProvider>
                </AuthProvider>
            </QueryClientProvider>
        </Router>
    );
}

const LegacyExitOptimizationRedirect = () => {
    const { jobId } = useParams();

    return <Navigate replace to={jobId ? `/user/aero/${jobId}` : '/user/aero'} />;
};

export default App;
